import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Table, Row, Col, Skeleton, Empty } from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  StopOutlined,
  CalendarOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import PagesHeader from "../../../components/PageHeader";
import ButtonBar from "../../../components/ButtonNavigation/index";
import SubscriptionSummaryBanner from "../../../components/InstallerSubscription/SubscriptionSummaryBanner";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import * as SubscriptionService from "../../../services/InstallerSubscriptionService";
import * as InstallerData from "../../../services/InstallerData";

const ManageInstallerSubscription = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [seatData, setSeatData] = useState(null);
  const [selectedSubForCancel, setSelectedSubForCancel] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const installersRes = await dispatch(InstallerData.getInstallerListing());
      const usedCount = Array.isArray(installersRes) ? installersRes.length : 0;
      const res = await dispatch(SubscriptionService.getInstallerSeatStatus(usedCount));
      setSeatData(res);
    } catch (err) {
      console.error("Error fetching seat status", err);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchStatus();
    window.scrollTo(0, 0);
  }, [fetchStatus]);

  const handleOpenCancel = (record) => {
    setSelectedSubForCancel(record);
    setShowCancelModal(true);
  };

  const handleCloseCancel = () => {
    setSelectedSubForCancel(null);
    setShowCancelModal(false);
  };

  const skeletonRows = [
    { id: "sk-1", isSkeleton: true },
    { id: "sk-2", isSkeleton: true },
    { id: "sk-3", isSkeleton: true },
  ];

  const columns = [
    {
      title: "Subscription ID",
      dataIndex: "id",
      key: "id",
      render: (id, record) =>
        record.isSkeleton ? (
          <Skeleton.Input style={{ width: 38, height: 18 }} active size="small" />
        ) : (
          <span style={{ fontWeight: 600, color: "#0f172a", fontSize: "14px" }}>
            #{id}
          </span>
        ),
    },
    {
      title: "Allocated Seats",
      dataIndex: "seats",
      key: "seats",
      render: (seats, record) =>
        record.isSkeleton ? (
          <Skeleton.Input style={{ width: 68, height: 18 }} active size="small" />
        ) : (
          <span style={{ fontWeight: 600, color: "#0284c7", fontSize: "14px" }}>
            {seats} {seats === 1 ? "Seat" : "Seats"}
          </span>
        ),
    },
    {
      title: "Plan Status",
      dataIndex: "subscription_status",
      key: "subscription_status",
      render: (status, record) =>
        record.isSkeleton ? (
          <Skeleton.Button style={{ width: 76, height: 24, borderRadius: 20 }} active size="small" />
        ) : (
          <span className="status-badge active">
            <span className="badge-dot"></span>
            {status || "ACTIVE"}
          </span>
        ),
    },
    {
      title: "Auto-Renewal",
      dataIndex: "is_auto_pay_canceled",
      key: "is_auto_pay_canceled",
      render: (isCanceled, record) =>
        record.isSkeleton ? (
          <Skeleton.Button style={{ width: 76, height: 24, borderRadius: 20 }} active size="small" />
        ) : isCanceled ? (
          <span className="status-badge canceled">
            <span className="badge-dot"></span>
            Canceled
          </span>
        ) : (
          <span className="status-badge active">
            <span className="badge-dot"></span>
            Active
          </span>
        ),
    },
    {
      title: "Billing Cycle (Renews / Ends)",
      key: "dates",
      render: (_, record) => {
        if (record.isSkeleton) {
          return <Skeleton.Input style={{ width: 150, height: 18 }} active size="small" />;
        }
        if (record.is_auto_pay_canceled && record.ends_at) {
          return (
            <span style={{ color: "#ea580c", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
              <CalendarOutlined />
              Ends on {record.ends_at}
            </span>
          );
        }
        if (record.renews_at) {
          return (
            <span style={{ color: "#334155", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
              <CalendarOutlined style={{ color: "#0284c7" }} />
              Renews on {record.renews_at}
            </span>
          );
        }
        return <span>{record.ends_at ? `Ends on ${record.ends_at}` : "-"}</span>;
      },
    },
    {
      title: "Purchased On",
      dataIndex: "created_at",
      key: "created_at",
      render: (date, record) =>
        record.isSkeleton ? (
          <Skeleton.Input style={{ width: 92, height: 18 }} active size="small" />
        ) : (
          <span style={{ color: "#64748b", fontSize: "13.5px" }}>
            {date || "-"}
          </span>
        ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) =>
        record.isSkeleton ? (
          <Skeleton.Button style={{ width: 72, height: 28, borderRadius: 6 }} active size="small" />
        ) : record.is_auto_pay_canceled ? (
          <span className="canceled-tag">
            <CloseCircleOutlined style={{ color: "#94a3b8" }} />
            Auto-Pay Off
          </span>
        ) : (
          <button
            type="button"
            className="cancel-action-btn"
            onClick={() => handleOpenCancel(record)}
            title="Cancel auto-renewal for this batch"
          >
            <StopOutlined style={{ fontSize: 13 }} />
            Cancel
          </button>
        ),
    },
  ];

  return (
    <div className="inspectionWrapper">
      <div className="inspectionHeader">
        <PagesHeader title="Manage Installer Subscription" />
      </div>

      <div className="buttonSection">
        <div className="container-xxl" style={{ paddingLeft: "32px", paddingRight: "32px" }}>
          <Row>
            <Col xs={24} md={24} className="text-end">
              <ButtonBar />
            </Col>
          </Row>
        </div>
      </div>

      <div className="inspectionWrapInner" style={{ padding: "20px 0 40px" }}>
        <div className="container-xxl" style={{ paddingLeft: "32px", paddingRight: "32px" }}>
          <div className="manage-sub-page">
            {/* Back Navigation Button */}
            <div
              className="back-nav-btn"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                color: "#475569",
                fontSize: "13.5px",
                fontWeight: 500,
                cursor: "pointer",
                marginBottom: "20px",
                padding: "7px 16px",
                borderRadius: "8px",
                background: "#ffffff",
                border: "1px solid #cbd5e1",
                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.03)",
                transition: "all 0.2s ease",
              }}
              onClick={() => navigate("/all-installers")}
            >
              <ArrowLeftOutlined />
              <span>Back to Installers</span>
            </div>

            {/* Header Action Section */}
            <div className="manage-header-actions">
              <div className="title-area">
                <h2>Installer Subscription</h2>
                <p>Manage your installer seat allocation, billing history, and auto-renewal settings.</p>
              </div>

              <div className="btn-group">
                <button
                  type="button"
                  className="btn-add-installer-premium"
                  onClick={() => navigate("/installer/subscription?mode=add_more")}
                >
                  <PlusOutlined style={{ fontSize: 15 }} />
                  Add More Seats
                </button>
              </div>
            </div>

            {/* Top Summary Banner */}
            <SubscriptionSummaryBanner seatData={seatData} loading={loading} />

            {/* Subscriptions Listing Card */}
            <div className="subscriptions-card">
              <div className="card-title-box">
                <h3>Your Active Subscriptions</h3>
                <p>Each record represents an individual seat batch with independent Stripe auto-renewal.</p>
              </div>

              <Table
                bordered
                columns={columns}
                dataSource={loading ? skeletonRows : (seatData?.subscriptions || [])}
                rowKey="id"
                pagination={false}
                scroll={{ x: 860 }}
                locale={{
                  emptyText: <Empty description="No subscription records found" />,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Modal */}
      <CancelSubscriptionModal
        visible={showCancelModal}
        onClose={handleCloseCancel}
        subscription={selectedSubForCancel}
        onSuccess={fetchStatus}
      />
    </div>
  );
};

export default ManageInstallerSubscription;
